import { AsyncPipe, CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatLegacySelectModule } from '@angular/material/legacy-select';
import { Store } from '@ngxs/store';
import { FullCalendarComponent, FullCalendarModule } from '@fullcalendar/angular';
import {
  Calendar as FullCalendar,
  CalendarOptions,
  EventClickArg,
  EventSourceInput,
  Calendar,
} from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import momentPlugin from '@fullcalendar/moment';
import rrulePlugin from '@fullcalendar/rrule';
import timeGridPlugin from '@fullcalendar/timegrid';
import moment from 'moment';
import { CalendarService } from 'app/modules/calendar/calendar.service';
import { RelatedToType } from '@shared/enums';
import { TaskState } from '@tasks/state/state';
import cloneDeep from 'lodash-es/cloneDeep';
import { CalendarEvent, CalendarSettings } from 'app/modules/calendar/calendar.types';
import { Task } from '@tasks/tasks.types';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { filter, map, noop, Observable } from 'rxjs';
import { TaskUtils } from '@tasks/tasks.utils';
import { TASK_WORKFLOW_STATUSES } from '@tasks/services/task-status.service';
import { TaskActions } from '@tasks/state/actions';
import { ActivatedRoute, Router } from '@angular/router';
import { title } from 'process';

@Component({
  selector: 'app-task-view-calendar',
  standalone: true,
  imports: [CommonModule, MatLegacyButtonModule, MatLegacySelectModule, MatIconModule, FullCalendarModule, AsyncPipe],
  template: `
    <div class="calendar-controls">
      <div class="calendar-controls__tools">
        <h4>
          {{ viewTitle }}
        </h4>
        <button class="previous" mat-icon-button (click)="previous()">
          <mat-icon [svgIcon]="'chevron_left'"></mat-icon>
        </button>
        <button class="next" mat-icon-button (click)="next()">
          <mat-icon [svgIcon]="'chevron_right'"></mat-icon>
        </button>
        <button class="lt-md:hidden" mat-icon-button (click)="today()">
          <mat-icon [svgIcon]="'today'"></mat-icon>
        </button>
      </div>
      <mat-form-field class="lt-md:hidden treo-mat-dense treo-mat-no-subscript view-changer">
        <mat-select (selectionChange)="changeView(viewChanger.value)" [value]="view" #viewChanger="matSelect">
          <mat-option [value]="'dayGridMonth'">Month</mat-option>
          <mat-option [value]="'timeGridWeek'">Week</mat-option>
          <mat-option [value]="'timeGridDay'">Day</mat-option>
          <mat-option [value]="'listYear'">Schedule</mat-option>
        </mat-select>
      </mat-form-field>
    </div>
    <div class="calendar">
      <full-calendar #fullCalendar [options]="calendarOptions" [events]="tasksEvents$ | async"></full-calendar>
    </div>
  `,
  styleUrl: './task-view-calendar.component.scss',
})
export class TaskViewCalendarComponent implements OnInit, AfterViewInit {
  private _fullCalendarApi: FullCalendar;
  private store = inject(Store);
  public viewTitle: string;
  private calendarSettings: CalendarSettings;
  public calendarOptions: CalendarOptions;
  private calendars: any[];
  private _projectsService = inject(ProjectsService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);

  viewTitleMemento;
  eventTimeFormat;
  views;
  view;
  project;
  event;
  calendarService = inject(CalendarService);
  private events: any[] = [];
  tasksEvents$: Observable<Task[]> = this.store.select(TaskState.getTasks).pipe(
    filter((task) => !!task),
    map((tasks) => {
      const flatTasks = TaskUtils.flattenTasks(tasks);
      const tasksEvents = cloneDeep(
        flatTasks.map((x) => ({
          extendedProps: x,
          title: x.title,
          end: x.due_date ? moment(x.due_date, moment.ISO_8601).add(1, 'd').format() : null,
          start: x.start_date,
          backgroundColor: TASK_WORKFLOW_STATUSES.find((y) => y.key === x.workflow_state)?.color,
        })),
      );
      return tasksEvents;
    }),
  );

  @ViewChild('fullCalendar') private _fullCalendar: FullCalendarComponent;

  public today(): void {
    this._fullCalendarApi.today();
    this.viewTitle = this._fullCalendarApi.view.title;
  }

  public changeView(view: 'dayGridMonth' | 'timeGridWeek' | 'timeGridDay' | 'listYear'): void {
    // Store the view
    this.view = view;

    // If the FullCalendar API is available...
    if (this._fullCalendarApi) {
      this._fullCalendarApi.changeView(view);
      this.viewTitle = this._fullCalendarApi.view.title;
      this.viewTitleMemento = this.viewTitle;
    }
  }

  ngAfterViewInit(): void {
    this.loadCalendar();
  }

  ngOnInit(): void {
    this.view = 'dayGridMonth';
    this.calendarService.getSettings().subscribe((settings) => {
      // Store the settings
      this.calendarSettings = settings;

      // Set the FullCalendar event time format based on the time format setting
      this.eventTimeFormat = {
        hour: settings.timeFormat === '12' ? 'numeric' : '2-digit',
        hour12: settings.timeFormat === '12',
        minute: '2-digit',
        meridiem: settings.timeFormat === '12' ? 'short' : false,
      };

      // Mark for check
      this.changeDetectorRef.markForCheck();
    });

    this.views = {
      dayGridMonth: {
        dayMaxEventRows: 3,
        eventTimeFormat: this.eventTimeFormat,
        fixedWeekCount: false,
      },
      timeGrid: {
        allDayContent: '',
        dayHeaderFormat: {
          weekday: 'short',
          day: 'numeric',
          omitCommas: true,
        },
        dayHeaderContent: (args) => {
          return {
            html: `
              <span class="fc-weekday">${moment(args.date).format('ddd')}</span>
              <span class="fc-date">${moment(args.date).format('D')}</span>
            `,
          };
        },
        slotDuration: '01:00:00',
        slotLabelFormat: this.eventTimeFormat,
      },
      listYear: {
        allDayContent: 'All day',
        eventTimeFormat: this.eventTimeFormat,
        listDayFormat: false,
        listDaySideFormat: false,
      },
    };

    this.calendarOptions = {
      contentHeight: '100%',
      headerToolbar: false,
      handleWindowResize: false,
      firstDay: this.calendarSettings?.startWeekOn,
      eventClick: this.onEventClick.bind(this),
      dateClick: this.onDateClick.bind(this),
      initialView: 'dayGridMonth',
      plugins: [dayGridPlugin, interactionPlugin, listPlugin, momentPlugin, rrulePlugin, timeGridPlugin],
    };
  }

  private onDateClick(dateClick): void {
    // Prepare the event
    const event = {
      id: null,
      calendarId: this.calendars[0].id,
      recurringEventId: null,
      isFirstInstance: false,
      title: '',
      description: '',
      start: moment(dateClick.date).startOf('day').toISOString(),
      end: moment(dateClick.date).endOf('day').toISOString(),
      duration: null,
      allDay: true,
      recurrence: null,
      range: {
        start: moment(dateClick.date).startOf('day').toISOString(),
        end: moment(dateClick.date).endOf('day').toISOString(),
      },
    };

    // Set the event
    this.event = event;

    // Set the el on dateClick for consistency
    dateClick.el = dateClick.dayEl;
  }

  public next(): void {
    this._fullCalendarApi.next();
    this.viewTitle = this._fullCalendarApi.view.title;
    const end = moment(this._fullCalendarApi.view.currentEnd);
    this.calendarService.prefetchFutureEvents(end).subscribe();
  }

  public previous(): void {
    this._fullCalendarApi.prev();
    this.viewTitle = this._fullCalendarApi.view.title;
    const start = moment(this._fullCalendarApi.view.currentStart);
    this.calendarService.prefetchPastEvents(start).subscribe();
  }

  private loadCalendar(): void {
    // Get the full calendar API
    this._fullCalendarApi = this._fullCalendar?.getApi();

    // Get the current view's title
    this.viewTitle = this._fullCalendarApi.view.title;
    this.viewTitleMemento = this.viewTitleMemento ?? this.viewTitle;

    // Get the view's current start and end dates, add/subtract
    // 60 days to create a ~150 days period to fetch the data for
    const viewStart = moment(this._fullCalendarApi.view.currentStart).subtract(60, 'days');
    const viewEnd = moment(this._fullCalendarApi.view.currentEnd).add(60, 'days');

    // Get events
    this.calendarService
      .getEvents(viewStart, viewEnd, true, { objectTypeIn: [RelatedToType.Task], assignedUserIdIn: [] })
      .subscribe();
  }

  private onEventClick(eventClick: EventClickArg): void {
    this.openTaskDetail(eventClick.event.extendedProps as Task);
  }

  openTaskDetail(task: Task) {
    this.store.dispatch(new TaskActions.GetTaskDetail(task.task_list_id, task.id)).subscribe(() => {
      this.router.navigate([{ outlets: { details: ['details', task.id] } }], {
        relativeTo: this.activatedRoute.parent,
      });
    });
  }

  private handleEvents(events: CalendarEvent[]) {
    this.events = events;
    this.changeDetectorRef.detectChanges();
  }
}
